import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Copy, Trash2, ClipboardPaste, Search, FileUp, CheckCircle2, Loader2, ShieldAlert, X } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { getCached, primeCache, fetchFresh } from "@/lib/cache";
import { useEditGate } from "@/components/EditGate";
import { useUnsaved } from "@/components/UnsavedGuard";
import { PasswordPrompt, ConfirmDelete } from "@/components/DeleteFlow";
import { downloadTemplate, downloadImportReport, importToastMessage } from "@/lib/importReport";
import { TagPicker } from "@/components/tags/TagPicker";
import { matchesTags } from "@/components/tags/TagBadges";
import { InlineTags } from "@/components/tags/InlineTags";

const EDIT_COLS = [
  { key: "name", label: "Item Name", type: "text", width: "w-56" },
  { key: "supplier", label: "Supplier", type: "text", width: "w-40" },
  { key: "brand", label: "Brand", type: "text", width: "w-32" },
  { key: "weight", label: "Weight (g)", type: "number", width: "w-28" },
  { key: "purchase_price", label: "Unit Price (₹)", type: "number", width: "w-32" },
];

let seq = 0;
const emptyRow = () => ({ id: null, _k: `new-${++seq}-${Date.now()}`, isNew: true, sku: "", name: "", supplier: "", brand: "", weight: "", purchase_price: "", tags: [], errors: {} });

const fromApi = (x) => ({ ...x, _k: x.id, isNew: false, weight: x.unit_conversion, errors: {} });

const toBody = (r) => ({
  name: (r.name || "").trim(), sku: r.sku || "", supplier: r.supplier || "", brand: r.brand || "",
  purchase_unit: r.purchase_unit || "pack", purchase_price: Number(r.purchase_price) || 0,
  base_unit: "g", unit_conversion: Number(r.weight) || 0, wastage_pct: 0,
  tags: r.tags || [],
});

const pricePerG = (r) => {
  const w = Number(r.weight) || 0;
  return w > 0 ? (Number(r.purchase_price) || 0) / w : 0;
};

export default function IngredientsPage() {
  const [rows, setRows] = useState(() => (getCached("ingredients") || []).map(fromApi));
  const [loading, setLoading] = useState(() => !getCached("ingredients"));
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [query, setQuery] = useState("");
  const [tagFilter, setTagFilter] = useState([]);
  const [deleteMode, setDeleteMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [pwOpen, setPwOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteAll, setDeleteAll] = useState(false);
  const [dirtyIds, setDirtyIds] = useState(() => new Set());
  const [savingEdits, setSavingEdits] = useState(false);
  const { requireEdit, unlocked } = useEditGate();
  const { setDirty } = useUnsaved();
  const fileRef = useRef(null);

  const newCount = rows.filter((r) => r.isNew).length;
  const savedRows = rows.filter((r) => !r.isNew);
  const dirtyCount = dirtyIds.size;

  useEffect(() => {
    fetchFresh("ingredients")
      .then((d) => setRows((prev) => [...d.map(fromApi), ...prev.filter((r) => r.isNew)]))
      .catch((e) => { if (!getCached("ingredients")) toast.error(apiError(e)); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const bits = [];
    if (newCount > 0) bits.push(`${newCount} item(s) not yet created`);
    if (dirtyCount > 0) bits.push(`${dirtyCount} edit(s) not saved`);
    const msg = bits.length ? `You have ${bits.join(" and ")}. Leave without saving?` : null;
    setDirty(msg);
    if (!msg) return;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => { window.removeEventListener("beforeunload", h); };
  }, [newCount, dirtyCount, setDirty]);

  useEffect(() => () => setDirty(null), [setDirty]);

  const setCell = (k, key, value) => setRows((p) => p.map((r) => {
    if (r._k !== k) return r;
    if (!r.isNew) setDirtyIds((s) => { if (s.has(r.id)) return s; const n = new Set(s); n.add(r.id); return n; });
    return { ...r, [key]: value, errors: { ...r.errors, [key]: undefined } };
  }));

  const gateFocus = (e) => {
    if (deleteMode) { e.target.blur(); return; }
    if (!unlocked) { e.target.blur(); requireEdit(() => {}); }
  };

  const saveExisting = async (row, password) => {
    if (!row.id || !row.name.trim()) return false;
    try {
      const { data } = await api.put(`/ingredients/${row.id}`, { ...toBody(row), password });
      setRows((p) => p.map((r) => (r._k === row._k ? { ...fromApi(data), _k: row._k } : r)));
      return true;
    } catch (e) { toast.error(apiError(e)); return false; }
  };

  const saveEdits = () => requireEdit(async (password) => {
    const dirty = rows.filter((r) => !r.isNew && dirtyIds.has(r.id));
    if (dirty.length === 0) return;
    setSavingEdits(true);
    let saved = 0;
    const failedIds = new Set();
    for (const r of dirty) {
      const ok = await saveExisting(r, password);
      if (ok) saved += 1; else failedIds.add(r.id);
    }
    setDirtyIds(failedIds);
    setSavingEdits(false);
    if (saved > 0) { toast.success(`${saved} edit(s) saved`); fetchFresh("ingredients"); }
  }, true);

  const focusRow = (k) => setTimeout(() => document.querySelector(`[data-cellid="${k}-name"]`)?.focus(), 50);

  const addRow = () => requireEdit(() => {
    const r = emptyRow();
    setRows((p) => [...p, r]);
    focusRow(r._k);
  });

  const onKeyDown = (e, row) => {
    if (e.key === "Enter" && row.isNew) {
      e.preventDefault();
      const r = emptyRow();
      setRows((p) => [...p, r]);
      focusRow(r._k);
    }
  };

  const deleteRow = (row) => requireEdit(() => {
    if (row.id) return;
    setRows((p) => p.filter((r) => r._k !== row._k));
  });

  const createItems = () => requireEdit(async (password) => {
    const existingNames = rows.filter((r) => !r.isNew).map((r) => r.name.trim().toLowerCase());
    const seen = [...existingNames];
    let anyError = false;
    const validated = rows.map((r) => {
      if (!r.isNew) return r;
      const errors = {};
      const name = (r.name || "").trim();
      if (!name) errors.name = "Item name is required";
      else if (seen.includes(name.toLowerCase())) errors.name = `'${name}' already exists`;
      else seen.push(name.toLowerCase());
      if (!(Number(r.purchase_price) > 0)) errors.purchase_price = "Unit price is required";
      if (!(Number(r.weight) > 0)) errors.weight = "Weight (g) is required";
      if (Object.keys(errors).length) anyError = true;
      return { ...r, errors };
    });
    setRows(validated);
    if (anyError) {
      toast.error("Some items have errors — fix the highlighted cells and click 'Create items' again");
      return;
    }
    setCreating(true);
    let created = 0;
    let failed = false;
    for (const r of validated.filter((x) => x.isNew)) {
      try {
        const { data } = await api.post("/ingredients", { ...toBody(r), password });
        created += 1;
        setRows((p) => p.map((x) => (x._k === r._k ? { ...fromApi(data), _k: r._k } : x)));
      } catch (e) {
        failed = true;
        const msg = apiError(e);
        setRows((p) => p.map((x) => (x._k === r._k ? { ...x, errors: { ...x.errors, name: msg } } : x)));
      }
    }
    setCreating(false);
    if (created > 0) { toast.success(`${created} item(s) created — SKUs assigned`); fetchFresh("ingredients"); }
    if (failed) toast.error("Some items failed — fix the highlighted cells and click 'Create items' again");
  }, true);

  const handlePaste = (e, colKey) => {
    const text = e.clipboardData.getData("text/plain");
    if (!text || (!text.includes("\t") && !text.includes("\n"))) return;
    e.preventDefault();
    requireEdit(() => {
      const startCol = EDIT_COLS.findIndex((c) => c.key === colKey);
      const matrix = text.replace(/\r/g, "").split("\n").filter((l) => l.trim() !== "").map((l) => l.split("\t"));
      const added = matrix.map((cells) => {
        const r = emptyRow();
        cells.forEach((val, ci) => {
          const col = EDIT_COLS[startCol + ci];
          if (!col) return;
          r[col.key] = col.type === "number" ? String(parseFloat(String(val).replace(/[₹,\s]/g, "")) || "") : val.trim();
        });
        return r;
      }).filter((r) => r.name.trim());
      if (!added.length) return toast.error("Nothing to paste — first pasted column should land on Item Name");
      setRows((p) => [...p, ...added]);
      toast.info(`${added.length} row(s) added — review and click "Create items"`);
    });
  };

  const importFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (importing) { toast.error("A bulk upload is already in progress — please wait"); return; }
    requireEdit(async () => {
      setImporting(true);
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/ingredients/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
        primeCache("ingredients", data.rows);
        setRows((p) => [...data.rows.map(fromApi), ...p.filter((r) => r.isNew)]);
        toast.success(`Import done: ${importToastMessage(data)}`, { duration: 10000 });
        if (data.errors?.length) data.errors.slice(0, 8).forEach((m) => toast.error(m, { duration: 10000 }));
        downloadImportReport("l0_raw_materials", data);
      } catch (err) { toast.error(apiError(err)); }
      finally { setImporting(false); }
    });
  };

  const copyAll = () => {
    const header = ["SKU Code", ...EDIT_COLS.map((c) => c.label), "UOM", "Price per g", "Tags"].join("\t");
    const body = visible.map((r) => [r.sku, ...EDIT_COLS.map((c) => r[c.key]), "g", pricePerG(r).toFixed(4), (r.tags || []).join(", ")].join("\t")).join("\n");
    navigator.clipboard.writeText(`${header}\n${body}`);
    toast.success("Copied grid to clipboard (TSV)");
  };

  const openDelete = () => {
    if (newCount > 0) return toast.error("Finish creating pending items first (or remove them) before deleting.");
    if (dirtyCount > 0) return toast.error("Save your pending edits first (or refresh to discard) before deleting.");
    if (savedRows.length === 0) return toast.error("Nothing to delete.");
    setPwOpen(true);
  };

  const onPwVerified = () => {
    setDeleteMode(true);
    setSelectedIds(new Set());
    setPwOpen(false);
    toast.info("Delete mode on — tick the items you want to remove, then click 'Delete selected'.");
  };

  const exitDeleteMode = () => { setDeleteMode(false); setSelectedIds(new Set()); };

  const toggleSelect = (id) => setSelectedIds((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const toggleSelectAll = () => {
    const visibleIds = visible.filter((r) => !r.isNew).map((r) => r.id);
    const allSelected = visibleIds.every((id) => selectedIds.has(id));
    setSelectedIds((s) => {
      const n = new Set(s);
      if (allSelected) visibleIds.forEach((id) => n.delete(id));
      else visibleIds.forEach((id) => n.add(id));
      return n;
    });
  };

  const askDelete = (all) => {
    if (!all && selectedIds.size === 0) return toast.error("No items selected.");
    setDeleteAll(all);
    setConfirmOpen(true);
  };

  const performDelete = async (password) => {
    try {
      const body = deleteAll ? { password, all: true } : { password, ids: Array.from(selectedIds) };
      const { data } = await api.post("/ingredients/bulk-delete", body);
      toast.success(`Deleted ${data.deleted} raw material${data.deleted === 1 ? "" : "s"}`);
      const fresh = await fetchFresh("ingredients");
      setRows([...fresh.map(fromApi), ...rows.filter((r) => r.isNew)]);
      setConfirmOpen(false);
      exitDeleteMode();
    } catch (e) { toast.error(apiError(e)); }
  };

  const visible = rows.filter(r => r.isNew || dirtyIds.has(r.id) || (matchesTags(r, tagFilter) && (!query.trim() || [r.name, r.sku, r.supplier, r.brand].join(" ").toLowerCase().includes(query.toLowerCase()))));
  const draftTags = rows.flatMap(r => r.tags || []);
  const errorList = rows.flatMap((r, i) => Object.values(r.errors || {}).filter(Boolean).map((m) => ({ row: i + 1, name: r.name || "(unnamed)", msg: m })));
  const visibleSavedIds = visible.filter((r) => !r.isNew).map((r) => r.id);
  const allVisibleSelected = visibleSavedIds.length > 0 && visibleSavedIds.every((id) => selectedIds.has(id));

  return (
    <div className="min-h-screen" data-testid="ingredients-page">
      <div className="sticky top-0 z-20 border-b border-zinc-200 bg-white/95 px-6 py-4 backdrop-blur md:px-8" data-testid="ingredients-sticky-header">
        <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">L0 · Raw Materials Master</h1>
            <p className="mt-1 flex items-center gap-1.5 text-sm text-zinc-500">
              <ClipboardPaste className="h-3.5 w-3.5" strokeWidth={1.5} />
              Paste from Google Sheets or import an Excel file. New items are saved only when you click "Create items".
            </p>
          </div>
          <div className="flex flex-wrap gap-2 no-print">
            <input ref={fileRef} data-testid="import-file-input" type="file" accept=".xlsx,.csv" className="hidden" onChange={importFile} />
            <button data-testid="download-l0-template-button" onClick={() => downloadTemplate("l0_raw_materials_template.csv",
              ["Item Name", "SKU (optional)", "Supplier", "Brand", "Weight (g)", "Unit Price (₹)"],
              [["Onion", "", "FreshVeg Traders", "", 1000, 100], ["Tomato", "", "FreshVeg Traders", "", 1000, 60]])}
              className="inline-flex items-center gap-1.5 rounded-sm border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50">
              <FileUp className="h-4 w-4 rotate-180" strokeWidth={1.5} /> Template
            </button>
            <button data-testid="import-excel-button" onClick={() => fileRef.current?.click()} disabled={importing || deleteMode}
              className="inline-flex items-center gap-1.5 rounded-sm border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50">
              {importing ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> : <FileUp className="h-4 w-4" strokeWidth={1.5} />}
              {importing ? "Importing…" : "Import Excel / CSV"}
            </button>
            <button data-testid="copy-ingredients-button" onClick={copyAll} disabled={deleteMode}
              className="inline-flex items-center gap-1.5 rounded-sm border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50 disabled:opacity-50">
              <Copy className="h-4 w-4" strokeWidth={1.5} /> Copy grid
            </button>
            <button data-testid="add-ingredient-button" onClick={addRow} disabled={importing || deleteMode}
              className="inline-flex items-center gap-1.5 rounded-sm border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50 disabled:opacity-50">
              <Plus className="h-4 w-4" strokeWidth={1.5} /> Add row
            </button>
            {!deleteMode ? (
              <button data-testid="open-delete-mode-button" onClick={openDelete} disabled={importing}
                className="inline-flex items-center gap-1.5 rounded-sm border border-rose-300 bg-white px-3 py-2 text-sm font-medium text-rose-600 transition-colors hover:bg-rose-50 disabled:opacity-50">
                <Trash2 className="h-4 w-4" strokeWidth={1.5} /> Delete raw materials
              </button>
            ) : (
              <button data-testid="cancel-delete-mode-button" onClick={exitDeleteMode}
                className="inline-flex items-center gap-1.5 rounded-sm border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50">
                <X className="h-4 w-4" strokeWidth={1.5} /> Cancel delete
              </button>
            )}
            {newCount > 0 && (
              <button data-testid="create-items-button" onClick={createItems} disabled={creating || deleteMode}
                className="inline-flex items-center gap-1.5 rounded-sm bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:opacity-50">
                <CheckCircle2 className="h-4 w-4" strokeWidth={1.5} /> {creating ? "Creating…" : `Create ${newCount} item${newCount > 1 ? "s" : ""}`}
              </button>
            )}
            {dirtyCount > 0 && (
              <button data-testid="save-edits-button" onClick={saveEdits} disabled={savingEdits || deleteMode}
                className="inline-flex items-center gap-1.5 rounded-sm bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-700 disabled:opacity-50">
                <CheckCircle2 className="h-4 w-4" strokeWidth={1.5} /> {savingEdits ? "Saving…" : `Save ${dirtyCount} edit${dirtyCount > 1 ? "s" : ""}`}
              </button>
            )}
          </div>
        </div>

        <p className="mb-3 text-xs text-zinc-500">
          Price per g = unit price ÷ weight. Press Enter in a new row to add another. Paste column order: Name, Supplier, Brand, Weight (g), Unit Price. Excel/CSV import needs header columns: Item Name, Price, Weight (SKU/Supplier/Brand optional). Edits to existing rows are held locally — click "Save edits" to commit them (leaving the page discards them); wastage &amp; yields are handled at L1.
        </p>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex min-w-0 max-w-md flex-1 basis-64 items-center gap-2 rounded-sm border border-zinc-300 bg-white px-3 py-2">
            <Search className="h-4 w-4 text-zinc-400" strokeWidth={1.5} />
            <input data-testid="ingredients-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name, SKU, supplier, brand…"
              className="w-full border-0 bg-transparent p-0 text-sm focus:outline-none focus:ring-0" />
          </div>
          <TagPicker testid="l0-tag-filter" filter value={tagFilter} onChange={setTagFilter} />
          <span data-testid="l0-filter-result-count" className="text-xs text-zinc-500">{visible.length} items</span>
          {deleteMode && (
            <div className="flex items-center gap-2 rounded-sm border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-700" data-testid="delete-mode-banner">
              <ShieldAlert className="h-4 w-4" strokeWidth={1.5} />
              <span>{selectedIds.size} selected</span>
              <button data-testid="bulk-delete-selected-button" onClick={() => askDelete(false)} disabled={selectedIds.size === 0}
                className="rounded-sm bg-rose-600 px-2 py-1 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-50">
                Delete selected
              </button>
              <button data-testid="bulk-delete-all-button" onClick={() => askDelete(true)}
                className="rounded-sm border border-rose-300 bg-white px-2 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-100">
                Delete ALL
              </button>
            </div>
          )}
        </div>
      </div>

      {importing && (
        <div className="border-b border-blue-200 bg-blue-50 px-6 py-3 md:px-8" data-testid="import-progress-banner">
          <div className="flex items-center gap-3 text-sm text-blue-800">
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
            <span className="font-medium">Processing your file…</span>
            <span className="text-blue-700/80">Please wait until this finishes. Do not upload another file.</span>
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-blue-100">
            <div className="h-full w-1/3 animate-[progress_1.4s_ease-in-out_infinite] rounded-full bg-blue-500" />
          </div>
        </div>
      )}

      <div className="px-6 pb-6 md:px-8">
        <div data-testid="l0-table-scroll" tabIndex={0} role="region" aria-label="Raw materials table" className={`overflow-x-auto border border-zinc-300 ${importing ? "pointer-events-none opacity-60" : ""}`}>
          <table data-testid="l0-table" className="w-max min-w-full border-collapse text-left [&_th]:whitespace-nowrap [&_td]:whitespace-nowrap">
            <thead>
              <tr className="bg-zinc-100">
                {deleteMode && (
                  <th className="border-b border-zinc-300 px-3 py-2 text-xs w-8">
                    <input type="checkbox" data-testid="select-all-checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} />
                  </th>
                )}
                <th className="border-b border-zinc-300 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-zinc-600 w-8">#</th>
                <th className="border-b border-l border-zinc-300 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-zinc-600 w-24">SKU Code</th>
                {EDIT_COLS.map((c) => (
                  <th key={c.key} className={`border-b border-l border-zinc-300 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-zinc-600 ${c.width}`}>{c.label}</th>
                ))}
                <th className="border-b border-l border-zinc-300 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-zinc-600 w-16">UOM</th>
                <th className="border-b border-l border-zinc-300 px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider text-zinc-600 w-32">Price per g</th>
                <th className="border-b border-l border-zinc-300 px-3 py-2 w-10"></th>
                <th data-testid="l0-tags-header" className="border-b border-l border-zinc-300 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-zinc-600">Tags</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={EDIT_COLS.length + (deleteMode ? 7 : 6)} className="px-3 py-8 text-center text-sm text-zinc-400">Loading…</td></tr>}
              {!loading && visible.length === 0 && (
                <tr><td data-testid="l0-filter-empty" colSpan={EDIT_COLS.length + (deleteMode ? 7 : 6)} className="px-3 py-8 text-center text-sm text-zinc-400">{query || tagFilter.length ? "No matching raw materials." : "No raw materials yet. Add a row, paste from Sheets, or import Excel."}</td></tr>
              )}
              {visible.map((r, idx) => {
                const isSelected = r.id && selectedIds.has(r.id);
                return (
                  <tr key={r._k} data-testid={`ingredient-row-${idx}`} className={`hover:bg-zinc-50 ${r.isNew ? "bg-blue-50/30" : ""} ${isSelected ? "bg-rose-50/50" : ""}`}>
                    {deleteMode && (
                      <td className="border-b border-zinc-200 px-3 py-2 text-center">
                        {!r.isNew && (
                          <input type="checkbox" data-testid={`select-row-checkbox-${idx}`} checked={isSelected} onChange={() => toggleSelect(r.id)} />
                        )}
                      </td>
                    )}
                    <td className="border-b border-zinc-200 px-3 py-2 font-mono text-[12px] text-zinc-400">{idx + 1}</td>
                    <td className="border-b border-l border-zinc-200 px-3 py-2 font-mono text-[13px] text-zinc-600" data-testid={`ingredient-sku-${idx}`}>
                      {r.isNew ? <span className="text-zinc-400 italic text-xs">auto on create</span> : r.sku}
                    </td>
                    {EDIT_COLS.map((c) => {
                      const err = r.errors?.[c.key];
                      return (
                        <td key={c.key} className={`border-b border-l px-3 py-2 ${err ? "border-rose-300 bg-rose-50 ring-1 ring-inset ring-rose-400" : "border-zinc-200 focus-within:bg-blue-50/40 focus-within:ring-1 focus-within:ring-inset focus-within:ring-blue-600"}`}
                          title={err || undefined}>
                          <input
                            data-testid={`ingredient-cell-${idx}-${c.key}`}
                            data-cellid={`${r._k}-${c.key}`}
                            className={`grid-cell-input ${c.type === "number" ? "text-right font-mono" : ""}`}
                            style={c.key === "name" ? { minWidth: `${Math.max(28, (r.name || "").length + 2)}ch` } : undefined}
                            type={c.type === "number" ? "number" : "text"}
                            step="any"
                            value={r[c.key] ?? ""}
                            disabled={deleteMode}
                            onFocus={gateFocus}
                            onChange={(e) => setCell(r._k, c.key, e.target.value)}
                            onKeyDown={(e) => onKeyDown(e, r)}
                            onPaste={(e) => handlePaste(e, c.key)}
                          />
                          {err && <div className="mt-0.5 text-[10px] leading-tight text-rose-600" data-testid={`ingredient-error-${idx}-${c.key}`}>{err}</div>}
                        </td>
                      );
                    })}
                    <td className="border-b border-l border-zinc-200 px-3 py-2 text-[13px] text-zinc-500">g</td>
                    <td className="border-b border-l border-zinc-200 px-3 py-2 text-right font-mono text-[13px] text-zinc-700" data-testid={`ingredient-cost-${idx}`}>
                      ₹{pricePerG(r).toFixed(4)}<span className="text-zinc-400">/g</span>
                    </td>
                    <td className="border-b border-l border-zinc-200 px-2 py-2 text-center">
                      {r.isNew && (
                        <button data-testid={`delete-ingredient-${idx}`} onClick={() => deleteRow(r)} title="Remove uncreated row" className="text-zinc-400 transition-colors hover:text-rose-600">
                          <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                        </button>
                      )}
                    </td>
                    <td className="border-b border-l border-zinc-200 px-3 py-2">
                      {r.isNew ? <TagPicker compact testid={`l0-tags-${idx}`} value={r.tags || []} onChange={tags => setCell(r._k, "tags", tags)} disabled={deleteMode || creating || savingEdits} requestOpen={cb => requireEdit(cb)} extraTags={draftTags} />
                        : <InlineTags item={r} collection="ingredients" testid={`l0-tags-${idx}`} disabled={deleteMode || creating || savingEdits}
                          onSaved={patch => setRows(previous => previous.map(row => row.id === patch.id ? { ...row, tags: patch.tags } : row))} />}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {errorList.length > 0 && (
          <div className="mt-3 border border-rose-200 bg-rose-50 p-3" data-testid="create-errors-panel">
            <div className="mb-1 text-xs font-semibold uppercase tracking-[0.05em] text-rose-700">Fix these before creating</div>
            {errorList.map((e, i) => (
              <div key={i} className="text-xs text-rose-600">Row {e.row} — {e.name}: {e.msg}</div>
            ))}
          </div>
        )}
      </div>

      <PasswordPrompt open={pwOpen} onClose={() => setPwOpen(false)} onVerified={onPwVerified}
        description="Enter the admin password to enable deletion. Deleting raw materials will break any L1 items tagged to them — you'll need to re-tag those L1 items to another L0." />
      <ConfirmDelete
        open={confirmOpen} onClose={() => setConfirmOpen(false)}
        onConfirm={performDelete}
        count={deleteAll ? savedRows.length : selectedIds.size}
        all={deleteAll}
        items={deleteAll ? savedRows : savedRows.filter((r) => selectedIds.has(r.id))}
        itemLabel="raw material"
        warning="Any L1 item tagged to a deleted raw material will show '(deleted)' until you re-tag it in the Ingredient Database."
      />
    </div>
  );
}
